import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { grey } from "@mui/material/colors";

import Section from "../../../layout/Section";
import CollapsibleColumnButton from "../../../tables/common/CollapsibleColumnButton";
import { collapsibleSubColumnSx } from "../../../tables/common/collapsibleColumnStyles";
import { fonts, colors } from "../../../../styles/theme";
import { formatCurrency } from "../../../../utils/formatters";
import { useRepaymentPlan } from "../hooks/useRepaymentPlan";
import { AggregatedYearEntry } from "../../../../types";

interface RepaymentSectionProps {
  /** Projekt-ID für den eigenständigen Daten-Fetch */
  projectId: string;
  /** Wird inkrementiert, um einen Reload auszulösen (z.B. nach Darlehens-Mutation) */
  reloadTrigger: number;
}

// Monatsnamen für aufgeklappte Spalten
const MONATSNAMEN = [
  "Jan",
  "Feb",
  "Mär",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
];

/**
 * RepaymentSection - Zins- und Tilgungsplan
 *
 * Features:
 * - Aufklappbare Jahre (→ Monate)
 * - Zeilen: Auszahlung, Zinsen, Tilgung, Restschuld
 */
const RepaymentSection: React.FC<RepaymentSectionProps> = ({
  projectId,
  reloadTrigger,
}) => {
  // Eigenständiger Daten-Hook – lädt den Tilgungsplan unabhängig von den anderen Sections
  const { repaymentPlanResponse, isLoading, isReloading } = useRepaymentPlan({
    projectId,
    reloadTrigger,
  });

  // State für aufgeklappte Jahre (Spalten)
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());

  // Daten aus Response extrahieren
  const jahre = useMemo(
    () => repaymentPlanResponse?.jahre || [],
    [repaymentPlanResponse],
  );

  // Toggle Jahr aufklappen
  const toggleYear = useCallback((betriebsjahr: number) => {
    setExpandedYears((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(betriebsjahr)) {
        newSet.delete(betriebsjahr);
      } else {
        newSet.add(betriebsjahr);
      }
      return newSet;
    });
  }, []);

  // Prüfe ob ein Jahr negative Werte vor Jahr 1 hat
  const hasNegativeYears = useMemo(() => {
    return jahre.some((j) => j.betriebsjahr < 1);
  }, [jahre]);

  // Berechne Summen
  const summen = useMemo(() => {
    const result = { auszahlung: 0, zinsen: 0, tilgung: 0 };

    jahre.forEach((jahr) => {
      result.auszahlung += jahr.auszahlung_summe;
      result.zinsen += jahr.zinsbetrag_summe;
      result.tilgung += jahr.tilgungsbetrag_summe;
    });

    return result;
  }, [jahre]);

  // Gemeinsame Zellen-Styles
  const cellStyles = {
    base: {
      fontFamily: fonts.monospace,
      whiteSpace: "nowrap" as const,
      py: 1.5,
      px: 1,
    },
    header: {
      fontWeight: fonts.weights.semiBold,
      backgroundColor: colors.background.tableHeader,
    },
    sticky: {
      position: "sticky" as const,
      left: 0,
      backgroundColor: "white",
      zIndex: 2,
      borderRight: `1px solid ${grey[300]}`,
    },
    stickyHeader: {
      position: "sticky" as const,
      left: 0,
      backgroundColor: colors.background.tableHeader,
      zIndex: 3,
      borderRight: `1px solid ${grey[300]}`,
    },
    summe: {
      fontWeight: fonts.weights.bold,
      backgroundColor: colors.background.tableHeader,
      borderLeft: `2px solid ${grey[400]}`,
    },
    // Dezenter Hintergrund für Jahre vor Betriebsbeginn (Bauphase)
    preOperation: {
      backgroundColor: colors.background.tableHeaderAlt, // Sehr dezentes Slate-Grau
    },
    preOperationHeader: {
      backgroundColor: colors.background.tableHeaderPreOp, // Etwas dunkler für Header
    },
    detailCell: {
      ...collapsibleSubColumnSx.cell,
      color: grey[700],
    },
    detailCellPreOperation: {
      ...collapsibleSubColumnSx.cell,
      backgroundColor: colors.background.tableHeaderPreOp,
      color: grey[700],
    },
    monthCell: {
      ...collapsibleSubColumnSx.cell,
      minWidth: 60,
    },
    monthCellPreOperation: {
      ...collapsibleSubColumnSx.cell,
      backgroundColor: colors.background.tableHeaderPreOp,
      minWidth: 60,
    },
  };

  // Bestimmt den Style für Jahre vor Betriebsbeginn (negative Jahre)
  const getPreOperationStyle = (
    betriebsjahr: number,
    isHeader: boolean = false,
    isDetail: boolean = false,
    isMonth: boolean = false,
  ) => {
    if (betriebsjahr < 1) {
      if (isMonth) return cellStyles.monthCellPreOperation;
      if (isDetail) return cellStyles.detailCellPreOperation;
      if (isHeader) return cellStyles.preOperationHeader;
      return cellStyles.preOperation;
    }
    return {};
  };

  // Initialer Loading State (voller Spinner, kein Tabellen-Inhalt)
  if (isLoading) {
    return (
      <Section title="Zins- und Tilgungsplan">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress size={28} />
        </Box>
      </Section>
    );
  }

  // Empty State
  if (!repaymentPlanResponse || jahre.length === 0) {
    return (
      <Section title="Zins- und Tilgungsplan">
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Noch kein Tilgungsplan vorhanden. Bitte erstellen Sie mindestens ein
            Darlehen.
          </Typography>
        </Box>
      </Section>
    );
  }

  // Render Jahr-Header mit Aufklapp-Button
  const renderYearHeader = (jahr: AggregatedYearEntry, index: number) => {
    const isExpanded = expandedYears.has(jahr.betriebsjahr);
    const prevJahr = index > 0 ? jahre[index - 1] : undefined;
    const isPreOperation = jahr.betriebsjahr < 1;

    // Header-Inhalt (wird bei negativen Jahren in Tooltip gewrapped)
    const headerContent = (
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {jahr.betriebsjahr}
        </Typography>
        <CollapsibleColumnButton
          isExpanded={isExpanded}
          onToggle={() => toggleYear(jahr.betriebsjahr)}
        />
      </Box>
    );

    return (
      <React.Fragment key={`header-${jahr.betriebsjahr}`}>
        <TableCell
          align="center"
          sx={{
            ...cellStyles.base,
            ...cellStyles.header,
            ...getPreOperationStyle(jahr.betriebsjahr, true),
            minWidth: 80,
            // Button nur bei Hover anzeigen
            "& .MuiIconButton-root": {
              opacity: 0,
              transition: "opacity 0.2s",
            },
            "&:hover .MuiIconButton-root": {
              opacity: 1,
            },
          }}
        >
          {isPreOperation ? (
            <Tooltip
              title="Jahr vor Betriebsbeginn (Bau- und Finanzierungsphase)"
              arrow
            >
              {headerContent}
            </Tooltip>
          ) : (
            headerContent
          )}
        </TableCell>

        {/* Monatsspalten wenn Jahr aufgeklappt (nur aktive Monate) */}
        {isExpanded &&
          jahr.monate?.map((m) => (
            <TableCell
              key={`header-${jahr.betriebsjahr}-${m.monat}`}
              align="right"
              sx={{
                ...cellStyles.base,
                ...cellStyles.header,
                ...cellStyles.monthCell,
                ...getPreOperationStyle(jahr.betriebsjahr, true),
                fontSize: collapsibleSubColumnSx.header.fontSize,
              }}
            >
              {MONATSNAMEN[m.monat - 1]}
            </TableCell>
          ))}
      </React.Fragment>
    );
  };

  // Render Wert-Zelle für ein Jahr (mit optionalen Monaten)
  const renderYearCell = (
    jahr: AggregatedYearEntry,
    index: number,
    getValue: (j: AggregatedYearEntry) => number,
    getMonthValue?: (j: AggregatedYearEntry, monatIndex: number) => number,
    isDetail: boolean = false,
  ) => {
    const isExpanded = expandedYears.has(jahr.betriebsjahr);
    const prevJahr = index > 0 ? jahre[index - 1] : undefined;
    const value = getValue(jahr);

    return (
      <React.Fragment key={`cell-${jahr.betriebsjahr}`}>
        <TableCell
          align="right"
          sx={{
            ...cellStyles.base,
            ...(isDetail ? cellStyles.detailCell : {}),
            ...getPreOperationStyle(jahr.betriebsjahr, false, isDetail),
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "medium" }}>
            {formatCurrency(value)}
          </Typography>
        </TableCell>

        {/* Monatswerte wenn Jahr aufgeklappt */}
        {isExpanded &&
          getMonthValue &&
          jahr.monate?.map((_, monatIndex) => (
            <TableCell
              key={`cell-${jahr.betriebsjahr}-${monatIndex}`}
              align="right"
              sx={{
                ...cellStyles.base,
                ...(isDetail ? cellStyles.detailCell : cellStyles.monthCell),
                ...getPreOperationStyle(
                  jahr.betriebsjahr,
                  false,
                  isDetail,
                  !isDetail,
                ),
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                {formatCurrency(getMonthValue(jahr, monatIndex))}
              </Typography>
            </TableCell>
          ))}

        {/* Leere Monate wenn keine Daten */}
        {isExpanded &&
          !getMonthValue &&
          jahr.monate?.map((m) => (
            <TableCell
              key={`cell-${jahr.betriebsjahr}-${m.monat}`}
              align="right"
              sx={{
                ...cellStyles.base,
                ...(isDetail ? cellStyles.detailCell : cellStyles.monthCell),
                ...getPreOperationStyle(
                  jahr.betriebsjahr,
                  false,
                  isDetail,
                  !isDetail,
                ),
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                —
              </Typography>
            </TableCell>
          ))}
      </React.Fragment>
    );
  };

  // Render Auszahlungs-Zeile
  const renderAuszahlungRow = () => (
    <TableRow>
      <TableCell sx={{ ...cellStyles.base, ...cellStyles.sticky }}>
        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
          Auszahlung
        </Typography>
      </TableCell>

      {jahre.map((jahr, index) =>
        renderYearCell(
          jahr,
          index,
          (j) => j.auszahlung_summe,
          (j, m) => j.monate?.[m]?.auszahlung || 0,
        ),
      )}

      <TableCell align="right" sx={{ ...cellStyles.base, ...cellStyles.summe }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {formatCurrency(summen.auszahlung)}
        </Typography>
      </TableCell>
    </TableRow>
  );

  // Render Zinsen-Zeile
  const renderZinsenRow = () => (
    <TableRow>
      <TableCell sx={{ ...cellStyles.base, ...cellStyles.sticky }}>
        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
          Zinsen
        </Typography>
      </TableCell>

      {jahre.map((jahr, index) =>
        renderYearCell(
          jahr,
          index,
          (j) => j.zinsbetrag_summe,
          (j, m) =>
            (j.monate?.[m]?.kreditzins_betrag || 0) +
            (j.monate?.[m]?.bereitstellungszins_betrag || 0),
        ),
      )}

      <TableCell align="right" sx={{ ...cellStyles.base, ...cellStyles.summe }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {formatCurrency(summen.zinsen)}
        </Typography>
      </TableCell>
    </TableRow>
  );

  // Render Tilgung-Zeile
  const renderTilgungRow = () => (
    <TableRow>
      <TableCell sx={{ ...cellStyles.base, ...cellStyles.sticky }}>
        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
          Tilgung
        </Typography>
      </TableCell>

      {jahre.map((jahr, index) =>
        renderYearCell(
          jahr,
          index,
          (j) => j.tilgungsbetrag_summe,
          (j, m) => j.monate?.[m]?.tilgungsbetrag || 0,
        ),
      )}

      <TableCell align="right" sx={{ ...cellStyles.base, ...cellStyles.summe }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {formatCurrency(summen.tilgung)}
        </Typography>
      </TableCell>
    </TableRow>
  );

  // Render Restschuld-Zeile (Footer)
  const renderRestschuldRow = () => (
    <TableRow sx={{ backgroundColor: colors.background.tableHeader }}>
      <TableCell
        sx={{
          ...cellStyles.base,
          ...cellStyles.sticky,
          backgroundColor: colors.background.tableHeader,
          fontWeight: fonts.weights.bold,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Restschuld
        </Typography>
      </TableCell>

      {jahre.map((jahr, index) => {
        const isExpanded = expandedYears.has(jahr.betriebsjahr);
        const prevJahr = index > 0 ? jahre[index - 1] : undefined;
        const restschuld = jahr.restschuld_jahresende;

        return (
          <React.Fragment key={`restschuld-${jahr.betriebsjahr}`}>
            <TableCell
              align="right"
              sx={{
                ...cellStyles.base,
                backgroundColor:
                  jahr.betriebsjahr < 1
                    ? colors.background.tableHeaderPreOp
                    : colors.background.tableHeader,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold" }}
                color={restschuld === 0 ? "success.main" : "inherit"}
              >
                {formatCurrency(restschuld)}
              </Typography>
            </TableCell>

            {/* Monatliche Restschulden wenn aufgeklappt */}
            {isExpanded &&
              jahr.monate?.map((monat, monatIndex) => (
                <TableCell
                  key={`restschuld-${jahr.betriebsjahr}-${monatIndex}`}
                  align="right"
                  sx={{
                    ...cellStyles.base,
                    ...cellStyles.monthCell,
                    backgroundColor:
                      jahr.betriebsjahr < 1
                        ? colors.background.tableHeaderPreOp
                        : colors.background.tableHeader,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold" }}
                    color={monat.restschuld === 0 ? "success.main" : "inherit"}
                  >
                    {formatCurrency(monat.restschuld)}
                  </Typography>
                </TableCell>
              ))}
          </React.Fragment>
        );
      })}

      <TableCell align="right" sx={{ ...cellStyles.base, ...cellStyles.summe }}>
        —
      </TableCell>
    </TableRow>
  );

  return (
    <Section title="Zins- und Tilgungsplan" noPadding>
      <Box sx={{ position: "relative" }}>
        {/* Dezentes Overlay beim Nachladen nach Mutation (Expand-State bleibt erhalten) */}
        {isReloading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table
            size="small"
            sx={{ minWidth: "max-content", borderCollapse: "collapse" }}
          >
            <TableHead>
              <TableRow>
                {/* Sticky Header für Zeilenlabel */}
                <TableCell
                  sx={{
                    ...cellStyles.base,
                    ...cellStyles.stickyHeader,
                    minWidth: 180,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Betriebsjahr
                  </Typography>
                </TableCell>

                {/* Jahr-Header mit Aufklapp-Buttons */}
                {jahre.map((jahr, index) => renderYearHeader(jahr, index))}

                {/* Summe-Header */}
                <TableCell
                  align="right"
                  sx={{
                    ...cellStyles.base,
                    ...cellStyles.header,
                    ...cellStyles.summe,
                    minWidth: 100,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Summe
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {renderAuszahlungRow()}
              {renderZinsenRow()}
              {renderTilgungRow()}
              {renderRestschuldRow()}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Section>
  );
};

export default RepaymentSection;
